import React from 'react';
import { Box, Container, Typography, Card, CardContent, CardHeader, Avatar, Chip, LinearProgress, Alert, IconButton, Tooltip, Paper, Stack, Divider, Grid } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, Refresh, Psychology, ShowChart,  Security, Timeline, SentimentNeutral } from '@mui/icons-material';
import { cryptoApi } from '../services/cryptoApi';
import { recommendationsApi } from '../services/recommendationsApi';

const DashboardPage = () => {
  const {
    data: cryptoData = [],
    isLoading: cryptoLoading,
    error: cryptoError,
    refetch: refetchCrypto,
  } = cryptoApi.endpoints.getAllCryptoCurrencies.useQuery();

  const {
    data: recommendationsData = [],
    isLoading: recommendationsLoading,
    error: recommendationsError,
    refetch: refetchRecommendations,
  } = recommendationsApi.endpoints.getAllLatestRecommendations.useQuery();

  const [refreshCryptoData] = cryptoApi.endpoints.refreshCryptoData.useMutation();

  const handleRefreshCrypto = async (cryptoCurrencyId) => {
    try {
      await refreshCryptoData(cryptoCurrencyId).unwrap();
    } catch (error) {
      console.error('Failed to refresh crypto data:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(value);
  };

  const formatMarketCap = (value) => {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return formatCurrency(value);
  };

  const getPriceChangeIcon = (change) => {
    if (change > 0) return <TrendingUp color="success" />;
    if (change < 0) return <TrendingDown color="error" />;
    return <TrendingFlat color="disabled" />;
  };

  const getPriceChangeColor = (change) => {
    if (change > 0) return 'success';
    if (change < 0) return 'error';
    return 'default';
  };

  const getTrendIcon = (trend) => {
    switch (trend?.toLowerCase()) {
      case 'bullish':
        return <TrendingUp color="success" />;
      case 'bearish':
        return <TrendingDown color="error" />;
      case 'sideways':
        return <TrendingFlat color="warning" />;
      default:
        return <ShowChart color="disabled" />;
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSentimentColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'bullish':
        return 'success';
      case 'bearish':
        return 'error';
      case 'neutral':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Create a map of recommendations by cryptocurrency ID
  const recommendationsMap = recommendationsData.reduce((acc, rec) => {
    acc[rec.cryptoCurrencyId] = rec;
    return acc;
  }, {});

  if (cryptoLoading || recommendationsLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
          <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
            Loading dashboard data...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (cryptoError || recommendationsError) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load dashboard data. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Crypto Trading Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          AI-powered cryptocurrency analysis and recommendations
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {cryptoData.map((crypto) => {
          const recommendation = recommendationsMap[crypto.cryptoCurrencyId];
          
          return (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={crypto.cryptoCurrencyId}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardHeader
                  avatar={
                    <Avatar
                      src={crypto.imageUrl}
                      alt={crypto.symbol}
                      sx={{ width: 40, height: 40 }}
                    >
                      {crypto.symbol?.charAt(0)}
                    </Avatar>
                  }
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6">{crypto.name}</Typography>
                      <Chip label={crypto.symbol} size="small" variant="outlined" />
                    </Box>
                  }
                  subheader={`Rank #${crypto.marketCapRank || 'N/A'}`}
                  action={
                    <Tooltip title="Refresh Data">
                      <IconButton
                        onClick={() => handleRefreshCrypto(crypto.cryptoCurrencyId)}
                        size="small"
                      >
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  }
                />
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack spacing={2}>
                    {/* Price Information */}
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h5" fontWeight="bold">
                          {formatCurrency(crypto.currentPrice)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getPriceChangeIcon(crypto.priceChangePercentage24h)}
                          <Typography
                            variant="body2"
                            color={getPriceChangeColor(crypto.priceChangePercentage24h)}
                            fontWeight="medium"
                          >
                            {crypto.priceChangePercentage24h?.toFixed(2)}%
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={1}>
                        <Grid xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Market Cap
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatMarketCap(crypto.marketCap)}
                          </Typography>
                        </Grid>
                        <Grid xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            24h Volume
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatMarketCap(crypto.totalVolume)}
                          </Typography>
                        </Grid>
                        <Grid xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            24h High
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {crypto.high24h ? formatCurrency(crypto.high24h) : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            24h Low
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {crypto.low24h ? formatCurrency(crypto.low24h) : 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* AI Recommendations */}
                    {recommendation ? (
                      <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Psychology color="primary" />
                          <Typography variant="subtitle2" fontWeight="bold">
                            AI Analysis
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(recommendation.dateGenerated).toLocaleDateString()}
                          </Typography>
                        </Box>

                        <Stack spacing={1.5}>
                          {/* Trend Analysis */}
                          {recommendation.trendAnalysis && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getTrendIcon(recommendation.trendAnalysis.trend)}
                                <Typography variant="body2">Trend</Typography>
                              </Box>
                              <Chip
                                label={recommendation.trendAnalysis.trend}
                                size="small"
                                color={
                                  recommendation.trendAnalysis.trend?.toLowerCase() === 'bullish'
                                    ? 'success'
                                    : recommendation.trendAnalysis.trend?.toLowerCase() === 'bearish'
                                    ? 'error'
                                    : 'warning'
                                }
                              />
                            </Box>
                          )}

                          {/* Risk Assessment */}
                          {recommendation.riskAssessment && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Security color="action" />
                                <Typography variant="body2">Risk Level</Typography>
                              </Box>
                              <Chip
                                label={recommendation.riskAssessment.level}
                                size="small"
                                color={getRiskLevelColor(recommendation.riskAssessment.level)}
                              />
                            </Box>
                          )}

                          {/* Sentiment Analysis */}
                          {recommendation.sentimentAnalysis && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SentimentNeutral color="action" />
                                <Typography variant="body2">Sentiment</Typography>
                              </Box>
                              <Chip
                                label={recommendation.sentimentAnalysis.level}
                                size="small"
                                color={getSentimentColor(recommendation.sentimentAnalysis.level)}
                              />
                            </Box>
                          )}

                          {/* Volatility Analysis */}
                          {recommendation.volatilityAnalysis && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Timeline color="action" />
                                <Typography variant="body2">Volatility</Typography>
                              </Box>
                              <Chip
                                label={recommendation.volatilityAnalysis.level}
                                size="small"
                                color={getRiskLevelColor(recommendation.volatilityAnalysis.level)}
                              />
                            </Box>
                          )}

                          {/* Technical Indicators */}
                          {recommendation.technicalIndicators && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Technical Indicators
                              </Typography>
                              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                <Grid xs={6}>
                                  <Typography variant="body2">
                                    RSI: {recommendation.technicalIndicators.rsi?.toFixed(2) || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid xs={6}>
                                  <Typography variant="body2">
                                    MACD: {recommendation.technicalIndicators.macd?.toFixed(2) || 'N/A'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          )}

                          {/* Price Predictions */}
                          {recommendation.pricePredictions && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Price Predictions
                              </Typography>
                              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                <Grid xs={4}>
                                  <Typography variant="body2" fontSize="0.75rem">
                                    1W: {recommendation.pricePredictions.oneWeek ? formatCurrency(recommendation.pricePredictions.oneWeek) : 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid xs={4}>
                                  <Typography variant="body2" fontSize="0.75rem">
                                    1M: {recommendation.pricePredictions.oneMonth ? formatCurrency(recommendation.pricePredictions.oneMonth) : 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid xs={4}>
                                  <Typography variant="body2" fontSize="0.75rem">
                                    3M: {recommendation.pricePredictions.threeMonths ? formatCurrency(recommendation.pricePredictions.threeMonths) : 'N/A'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    ) : (
                      <Paper sx={{ p: 2, bgcolor: 'background.default', textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No AI recommendations available
                        </Typography>
                      </Paper>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {cryptoData.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No cryptocurrency data available
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default DashboardPage;
